import { DndContext } from "@dnd-kit/core";
import { Box } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "src/components/PageHeader/PageHeader";
import {
  useAllDeviceDocumentsIndex,
  useGetFolders,
  useGetFunctionalGroups,
  useGetUsersByOrg,
  useGlobalDeviceContext,
} from "src/hooks";
import { getLatestDocumentVersion } from "src/utils/documents";
import { buildDocumentsPageBreadcrumbItems } from "src/utils/documentsBreadcrumbs";
import { buildDocumentRoute } from "src/utils/navigation";
import {
  buildDocumentsPageFullList,
  getDocumentsPageDisplayContent,
  type DocumentsPageItem,
} from "./Documents.utils";
import { DocumentsPageBreadcrumb } from "./DocumentsPageBreadcrumb";
import { DocumentsPageViewToggle } from "./DocumentsPageViewToggle";
import { DocumentsTable } from "./DocumentsTable";
import {
  buildDocumentFilterRecords,
  filterDocumentsPageItems,
} from "./DocumentsToolbar/Documents.filtering";
import { DocumentsToolbar } from "./DocumentsToolbar/DocumentsToolbar";
import { useDocumentsFilters } from "./DocumentsToolbar/useDocumentsFilters";
import type { DocumentsTableItemId } from "./TableDnD/DocumentsTable.dnd";
import { useDocumentsTableMoveController } from "./TableDnD/useDocumentsTableMoveController";

type DocumentsPageDocumentItem = Extract<
  DocumentsPageItem,
  { kind: "document" }
>;

type DocumentsViewProps = {
  orgId: string;
};

/**
 * The default mode of the documents page: breadcrumb header plus the
 * folders/documents table with search, filtering and drag-to-move. The
 * DndContext spans the header because breadcrumb items are drop targets for
 * moving documents up the folder tree.
 */
export const DocumentsView = ({ orgId }: DocumentsViewProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [selectedTableItemIds, setSelectedTableItemIds] = useState<
    DocumentsTableItemId[]
  >([]);
  const folderId = searchParams.get("folderId");
  const queryDeviceId = searchParams.get("deviceId");
  const { activeGlobalDeviceId } = useGlobalDeviceContext();
  const {
    currentDeviceId,
    currentTemplatesConfig,
    devices,
    documentQueryDeviceIds,
    documents,
    getDocumentNavigationDeviceId,
    getTemplatesConfigForDevice,
    isLoading: isDocumentsIndexLoading,
    resolveTemplateForDocument,
  } = useAllDeviceDocumentsIndex({
    enabled: Boolean(orgId),
    orgId,
    preferredDeviceId: activeGlobalDeviceId,
    queryDeviceId,
    routeDeviceId: "",
  });
  const foldersQuery = useGetFolders({
    enabled: Boolean(orgId),
    orgId,
  });
  const { data: functionalGroups } = useGetFunctionalGroups(orgId);
  const usersQuery = useGetUsersByOrg({
    enabled: Boolean(orgId),
    orgId,
  });
  const users = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);
  const folders = useMemo(() => foldersQuery.data ?? [], [foldersQuery.data]);
  const breadcrumbItems = useMemo(
    () =>
      buildDocumentsPageBreadcrumbItems({
        activeFolderId: folderId,
        folders,
        orgId,
      }),
    [folderId, folders, orgId]
  );
  const isBreadcrumbItemsLoading =
    foldersQuery.isLoading && foldersQuery.data === undefined;
  const isUsersLoading = usersQuery.isLoading && usersQuery.data === undefined;
  const isDocumentsTableLoading =
    isDocumentsIndexLoading ||
    (foldersQuery.isLoading && foldersQuery.data === undefined) ||
    isUsersLoading;
  const fullList = useMemo(
    () =>
      isUsersLoading
        ? []
        : buildDocumentsPageFullList({
            devices,
            documents,
            folders,
            resolveTemplateForDocument,
            users,
          }),
    [
      devices,
      documents,
      folders,
      isUsersLoading,
      resolveTemplateForDocument,
      users,
    ]
  );
  const moveController = useDocumentsTableMoveController({
    currentDeviceId,
    documentQueryDeviceIds,
    folders,
    fullList,
    orgId,
    selectedItemIds: selectedTableItemIds,
  });
  const displayContent = useMemo(
    () =>
      getDocumentsPageDisplayContent({
        folderId,
        fullList,
      }),
    [folderId, fullList]
  );
  const searchableContent = search.trim() ? fullList : displayContent;
  const documentFilterRecords = useMemo(
    () =>
      buildDocumentFilterRecords({
        functionalGroups,
        items: searchableContent,
        resolveTemplateForDocument,
        templatesConfig: undefined,
      }),
    [functionalGroups, resolveTemplateForDocument, searchableContent]
  );
  const filterControls = useDocumentsFilters({
    devices,
    functionalGroups,
    users,
  });
  const filteredDisplayContent = useMemo(
    () =>
      filterDocumentsPageItems({
        filterRecords: documentFilterRecords,
        filters: filterControls.filters,
        items: searchableContent,
        search,
      }),
    [documentFilterRecords, filterControls.filters, search, searchableContent]
  );
  const getDocumentItemRoute = useCallback(
    (item: DocumentsPageDocumentItem) => {
      const deviceId = getDocumentNavigationDeviceId(item.document);

      if (!deviceId) {
        return null;
      }

      const latestVersion = getLatestDocumentVersion(item.document);

      return buildDocumentRoute({
        deviceId,
        documentId: item.document.id,
        orgId,
        searchParams,
        templateKey: item.document.templateKey,
        tiptapVersion: latestVersion?.tiptapVersion,
        versionId: latestVersion?.id,
      });
    },
    [getDocumentNavigationDeviceId, orgId, searchParams]
  );
  const handleNavigateToItem = useCallback(
    (item: DocumentsPageItem) => {
      if (item.kind === "folder") {
        setSearch("");
        setSearchParams((currentSearchParams) => {
          const nextSearchParams = new URLSearchParams(currentSearchParams);
          nextSearchParams.set("folderId", item.id);

          return nextSearchParams;
        });
        return;
      }

      const route = getDocumentItemRoute(item);

      if (!route) {
        return;
      }

      navigate(route);
    },
    [getDocumentItemRoute, navigate, setSearchParams]
  );
  const handleOpenDocumentInNewTab = useCallback(
    (item: DocumentsPageDocumentItem) => {
      const route = getDocumentItemRoute(item);

      if (!route) {
        return;
      }

      window.open(route, "_blank", "noopener,noreferrer");
    },
    [getDocumentItemRoute]
  );

  return (
    <DndContext
      cancelDrop={moveController.cancelDrop}
      onDragCancel={moveController.handleDragCancel}
      onDragEnd={moveController.handleDragEnd}
      onDragStart={moveController.handleDragStart}
      sensors={moveController.sensors}
    >
      <PageHeader
        pageTitleComponent={
          <DocumentsPageBreadcrumb
            disabled={isDocumentsTableLoading || moveController.isMoving}
            isLoading={isBreadcrumbItemsLoading}
            items={breadcrumbItems}
            moveController={moveController}
          />
        }
        rightButtons={<DocumentsPageViewToggle />}
        rightPanelToggleRequiresDevice={false}
      />

      <Box
        data-display-content-count={displayContent.length}
        data-visible-content-count={filteredDisplayContent.length}
        data-full-list-count={fullList.length}
        data-is-loading={isDocumentsTableLoading}
        data-testid="documents-page-body"
        sx={{
          backgroundColor: "#FFFFFF",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <DocumentsToolbar
          currentDeviceId={currentDeviceId}
          devices={devices}
          documents={documents}
          filterControls={filterControls}
          folderId={folderId}
          getTemplatesConfigForDevice={getTemplatesConfigForDevice}
          onSearchChange={setSearch}
          search={search}
          templatesConfig={currentTemplatesConfig}
        />
        <DocumentsTable
          currentDeviceId={currentDeviceId}
          documentQueryDeviceIds={documentQueryDeviceIds}
          documents={documents}
          folders={folders}
          isLoading={isDocumentsTableLoading}
          items={filteredDisplayContent}
          moveController={moveController}
          onOpenDocumentInNewTab={handleOpenDocumentInNewTab}
          onOpenItem={handleNavigateToItem}
          onSelectedItemIdsChange={setSelectedTableItemIds}
          orgId={orgId}
        />
      </Box>
    </DndContext>
  );
};
